import React, { useEffect, useState } from 'react';
import { useReactTable, flexRender } from '@tanstack/react-table';
import { getCoreRowModel } from '@tanstack/table-core';
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const TodayOrders = () => {
  const [orderData, setOrderData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(SummaryApi.allOrder.url, {
        method: SummaryApi.allOrder.method,
        headers: {
          'Authorization': SummaryApi.bearerToken,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch orders');

      const data = await response.json();
      setOrderData((data.data || []).reverse());

    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filterOrders = () => {
    const today = new Date().toISOString().split('T')[0];
    return orderData.filter((o) => {
      const date = new Date(o.order_date).toISOString().split('T')[0];
      return date === today &&
        (searchTerm === '' ||
          o.order_code?.toLowerCase().includes(searchTerm.toLowerCase()));
    });
  };

  const filteredOrders = filterOrders();

  const handleEdit = (order) => {
     navigate(`/admin-panel/orders/edit/${order.order_id}`, { state: { orderData: order } });
  };

  const columns = [
    {
      header: 'Order Code',
      accessorKey: 'order_code',
    },
    {
      header: 'Customer',
      accessorKey: 'customer_name',
    },
    {
      header: 'Mobile',
      accessorKey: 'mobile_number',
    },
    {
      header: 'Order Amount',
      accessorKey: 'order_amount',
      cell: ({ getValue }) => `₹ ${getValue()}`,
    },
    {
      header: 'Total Amount',
      accessorKey: 'total_amount',
      cell: ({ getValue }) => `₹ ${getValue()}`,
    },
    {
      header: 'Order Status',
      accessorKey: 'order_status',
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
            {value}
          </span>
        );
      },
    },
    {
      header: 'Address',
      accessorKey: 'order_address',
      cell: ({ getValue }) => {
        const text = getValue();
        return <span className="truncate block max-w-[200px]">{text}</span>;
      },
    },
    {
      header: 'Action',
      cell: ({ row }) => (
        <button
          onClick={() => handleEdit(row.original)}
          className="bg-blue-500 text-white px-3 py-1 rounded"
        >
          View
        </button>
      ),
    },
  ];

  const table = useReactTable({
    data: filteredOrders,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const downloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredOrders);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TodayOrders');
    XLSX.writeFile(wb, 'TodayOrders.xlsx');
  };

  return (
    <div className='bg-white p-4 rounded-lg shadow'>
      <h2 className='text-xl font-semibold mb-4'>Today's Orders</h2>

      <input
        type="text"
        placeholder="Search Order Code..."
        className="border px-3 py-2 rounded w-full mb-4"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <button
        onClick={downloadExcel}
        className="mb-4 px-4 py-2 bg-green-500 text-white rounded"
      >
        Download Excel
      </button>

      {loading ? (
        <p>Loading...</p>
      ) : filteredOrders.length === 0 ? (
        <p>No orders for today</p>
      ) : (
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th key={header.id} className="p-2 text-left">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="p-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TodayOrders;
